import { invariant } from '@epic-web/invariant'
import { Promisable } from 'type-fest'

import {
  isBlockWithChildren,
  isComponentBlock,
  isInstanceBlock,
  isListBlock,
  Block,
  isDocumentBlock,
  normalizeStringPath,
  isVariantSetBlock,
  isRichTextBlock,
  isWorkflowBlock,
  InstanceBlock,
} from '@basehub/data/replicache/index'
import { idToSourceBlockId } from '../mutators/copy-on-write'
import { getNewBlockDataByType } from '@basehub/data/replicache/helpers/get-new-block-data-by-type'

type ValidationResult = { isValid: true } | { isValid: false; reason: string }

/**
 * Starts from an "edge" block of type `instance` or `list`, and makes sure
 * they are nested correctly, and thus have a valid path.
 * Correct use for this function would be when syncing instances.
 */
export const validateBlockNestingPromise = async <
  ShouldThrow extends boolean = true,
>({
  block,
  path,
  shouldThrowIfInvalid,
  fetchBlock,
  fetchChildren,
}: {
  block: Block
  path: string
  shouldThrowIfInvalid?: ShouldThrow
  fetchBlock: (idOrSourceBlockId: string) => Promisable<Block | undefined>
  fetchChildren: (sourceBlockId: string) => Promisable<Block[]>
}): Promise<
  ShouldThrow extends true ? { isValid: true } : ValidationResult
> => {
  if (
    !isInstanceBlock(block) &&
    !isListBlock(block) &&
    !isDocumentBlock(block) &&
    !isComponentBlock(block) &&
    !isVariantSetBlock(block) &&
    !isWorkflowBlock(block)
  ) {
    // no issues with other block types
    return { isValid: true }
  }

  const normalizedPath = normalizeStringPath({
    path,
    edgeId: block.id,
    format: 'array',
    includeEdge: false,
  })

  const blockCache = new Map<string, Block>()
  const getBlockOrCache = async (id: string) => {
    if (blockCache.has(id)) {
      return blockCache.get(id) as Block
    }
    let block
    try {
      block = await fetchBlock(id)
    } catch {
      // ignore
    }
    if (!block) {
      // we assume that it's a nested instance which is virtualized
      block = {
        ...getNewBlockDataByType({
          type: 'instance',
          id,
          sourceBlockId: id,
          typeOptions: {
            targetBlockId: '',
          },
        }),
        __virtual: true,
      } as InstanceBlock & { __virtual: true }
    }
    blockCache.set(id, block)
    return block
  }

  const blocksOnPath = await Promise.all(
    normalizedPath.map(async (id) => await getBlockOrCache(id))
  )

  /**
   * Main switch statement. Where the logic starts.
   */
  let result: ValidationResult | null = null

  // check all blocksOnPath match certain criteria
  while (!result && blocksOnPath.length > 0) {
    const parentBlock = blocksOnPath.pop()
    if (!parentBlock) continue
    const grandParentBlock = blocksOnPath[blocksOnPath.length - 1]

    if (isComponentBlock(block)) {
      // 1.1. components cannot be contained in instances
      if (isInstanceBlock(parentBlock)) {
        result = {
          isValid: false,
          reason: `Component blocks cannot be descendants of instances.`,
        }
        break
      }
      continue
    }

    if (isComponentBlock(parentBlock)) {
      // 2.1. documents can not be contained in components
      if (isDocumentBlock(block)) {
        result = {
          isValid: false,
          reason: `Document blocks cannot be descendants of components.`,
        }
        break
      } else if (isVariantSetBlock(block) || isWorkflowBlock(block)) {
        result = {
          isValid: false,
          reason: `${block.type} blocks cannot be descendants of components.`,
        }
        break
      }

      // 2.2. parent can not be the same as target
      else if (
        idToSourceBlockId(parentBlock) === block.typeOptions.targetBlockId
      ) {
        result = {
          isValid: false,
          reason: `Blocks can not have their target as their parent.`,
        }
        break
      }

      // 2.3. parents can not be childs of the target block.
      if (
        grandParentBlock &&
        idToSourceBlockId(grandParentBlock) === block.typeOptions.targetBlockId
      ) {
        result = {
          isValid: false,
          reason: `Blocks can not have their target be their grandparent.`,
        }
        break
      }

      const targetBlock = await getBlockOrCache(block.typeOptions.targetBlockId)
      invariant(
        isBlockWithChildren(targetBlock),
        'targetBlock should be with children'
      )

      const promises = (
        await fetchChildren(idToSourceBlockId(targetBlock))
      ).map(async (child) => {
        const childBlock = await getBlockOrCache(child.id)
        // 2.4. targets can not have an instance of this component
        if (childBlock.type === 'instance' || childBlock.type === 'list') {
          // Use idToSourceBlockId to handle CoW scenarios where parentBlock.id differs from sourceBlockId
          if (
            childBlock.typeOptions.targetBlockId ===
            idToSourceBlockId(parentBlock)
          ) {
            result = {
              isValid: false,
              reason: `Target block can not have an instance of parent, which is a component.`,
            }
          }
          return
        }
      })

      await Promise.all(promises)
      // done for this block.
    } else if (isInstanceBlock(parentBlock)) {
      // 2.5. documents can not be contained in instances
      if (isDocumentBlock(block)) {
        result = {
          isValid: false,
          reason: `Document blocks can not be descendant of instances.`,
        }
        break
      } else if (isVariantSetBlock(block) || isWorkflowBlock(block)) {
        result = {
          isValid: false,
          reason: `${block.type} blocks can not be descendant of components.`,
        }
        break
      }
    } else if (isRichTextBlock(parentBlock)) {
      if (isVariantSetBlock(block) || isWorkflowBlock(block)) {
        result = {
          isValid: false,
          reason: `${block.type} blocks can not be descendant of rich text blocks.`,
        }
        break
      }
    } else if (isListBlock(parentBlock)) {
      // not sure if its valid or not at this point. pass to the next block.
      continue
    } else {
      // if at some point we haven't broken yet, and we encounter a document block,
      // then this acts as a "structure rupture" — components don't persist documents' structure
      result = { isValid: true }
      break
    }
  }

  if (!result) {
    result = { isValid: true }
  }

  invariant(result, 'result was not found')

  if (shouldThrowIfInvalid && result.isValid === false) {
    throw new Error(result.reason)
  }

  return result as ShouldThrow extends true
    ? { isValid: true }
    : ValidationResult
}

/**
 * Starts from an "edge" block of type `instance` or `list`, and makes sure
 * they are nested correctly, and thus have a valid path.
 * Correct use for this function would be when syncing instances.
 */
export const validateBlockNesting = <ShouldThrow extends boolean = true>({
  block,
  path,
  shouldThrowIfInvalid,
  fetchBlock,
  fetchChildren,
}: {
  block: Block
  path: string
  shouldThrowIfInvalid?: ShouldThrow
  fetchBlock: (id: string) => Block | null | undefined
  fetchChildren: (sourceBlockId: string) => Block[]
}): ShouldThrow extends true ? { isValid: true } : ValidationResult => {
  if (
    !isInstanceBlock(block) &&
    !isListBlock(block) &&
    !isDocumentBlock(block) &&
    !isVariantSetBlock(block) &&
    !isWorkflowBlock(block)
  ) {
    // no issues with other block types
    return { isValid: true }
  }

  const normalizedPath = normalizeStringPath({
    path,
    edgeId: block.id,
    format: 'array',
    includeEdge: false,
  })

  const blockCache = new Map<string, Block>()
  const getBlockOrCache = (id: string) => {
    if (blockCache.has(id)) return blockCache.get(id) as Block
    let block
    try {
      block = fetchBlock(id)
    } catch {
      // ignore
    }

    if (!block) {
      // we assume that it's a nested instance which is virtualized
      block = {
        ...getNewBlockDataByType({
          type: 'instance',
          id,
          sourceBlockId: id,
          typeOptions: {
            targetBlockId: '',
          },
        }),
        __virtual: true,
      } as InstanceBlock & { __virtual: true }
    }
    blockCache.set(id, block)
    return block
  }

  const blocksOnPath = normalizedPath.map((id) => getBlockOrCache(id))

  // main switch statement. Where the logic starts.
  let result: ValidationResult | null = null

  // check all blocksOnPath match certain criteria
  while (!result && blocksOnPath.length > 0) {
    const parentBlock = blocksOnPath.pop()
    invariant(parentBlock, 'parentBlock was not found')
    const grandParentBlock = blocksOnPath[blocksOnPath.length - 1]

    if (isComponentBlock(parentBlock)) {
      // 1. documents can not be contained in components
      if (isDocumentBlock(block)) {
        result = {
          isValid: false,
          reason: `Document blocks cannot be descendants of components.`,
        }
        break
      } else if (isVariantSetBlock(block) || isWorkflowBlock(block)) {
        result = {
          isValid: false,
          reason: `${block.type} blocks cannot be descendants of components.`,
        }
        break
      }

      // 2. parent can not be the same as target
      if (idToSourceBlockId(parentBlock) === block.typeOptions.targetBlockId) {
        result = {
          isValid: false,
          reason: `Blocks can not have their target as their parent.`,
        }
        break
      }

      // 3. parents can not be childs of the target block.
      if (
        grandParentBlock &&
        idToSourceBlockId(grandParentBlock) === block.typeOptions.targetBlockId
      ) {
        result = {
          isValid: false,
          reason: `Blocks can not have their target be their grandparent.`,
        }
        break
      }

      const targetBlock = getBlockOrCache(block.typeOptions.targetBlockId)
      invariant(
        isBlockWithChildren(targetBlock),
        'targetBlock should be with children'
      )

      fetchChildren(idToSourceBlockId(targetBlock)).map((child) => {
        const childBlock = getBlockOrCache(child.id)
        // 4. targets can not have an instance of this component
        if (childBlock.type === 'instance' || childBlock.type === 'list') {
          // Use idToSourceBlockId to handle CoW scenarios where parentBlock.id differs from sourceBlockId
          if (
            childBlock.typeOptions.targetBlockId ===
            idToSourceBlockId(parentBlock)
          ) {
            result = {
              isValid: false,
              reason: `Target block can not have an instance of parent, which is a component.`,
            }
          }
          return
        }
      })
      // done for this block.
    } else if (isInstanceBlock(parentBlock)) {
      // 5. documents can not be contained in instances
      if (isDocumentBlock(block)) {
        result = {
          isValid: false,
          reason: `Document blocks cannot be descendants of instances.`,
        }
        break
      } else if (isVariantSetBlock(block) || isWorkflowBlock(block)) {
        result = {
          isValid: false,
          reason: `${block.type} blocks cannot be descendants of components.`,
        }
        break
      }
    } else if (isListBlock(parentBlock)) {
      // not sure if its valid or not at this point. pass to the next block.
      continue
    } else if (isRichTextBlock(parentBlock)) {
      if (isVariantSetBlock(block) || isWorkflowBlock(block)) {
        result = {
          isValid: false,
          reason: `${block.type} blocks cannot be descendants of rich text blocks.`,
        }
        break
      }
    } else {
      // if at some point we haven't broken yet, and we encounter a document block,
      // then this acts as a "structure rupture" — components don't persist documents' structure
      result = { isValid: true }
      break
    }
  }

  if (!result) {
    result = { isValid: true }
  }

  invariant(result, 'result was not found')

  if (shouldThrowIfInvalid && result.isValid === false) {
    throw new Error(result.reason)
  }

  return result as ShouldThrow extends true
    ? { isValid: true }
    : ValidationResult
}

export const getBlockDisplayData = ({
  block,
  parentBlock,
  ignoreDisplayTitle,
}: {
  block: Block
  parentBlock: Block | undefined | null
  ignoreDisplayTitle?: boolean
}) => {
  const displayTitle = (() => {
    if (block.type !== 'component') {
      return block.title || 'Untitled'
    } else if (block.typeOptions.displayTitle && !ignoreDisplayTitle) {
      return block.typeOptions.displayTitle
    } else if (block.title) {
      return block.title
    } else if (parentBlock && parentBlock.type === 'list' && block.hidden) {
      return `"${parentBlock.title || 'Untitled Collection'}" Template`
    } else {
      return 'Untitled'
    }
  })()

  const displayDescription = (() => {
    if (block && block.type === 'component') {
      return block.typeOptions.displayDescription
    } else {
      return undefined
    }
  })()

  const isListTemplate =
    block.type === 'component' &&
    parentBlock?.type === 'list' &&
    (block.id === parentBlock.typeOptions.targetBlockId ||
      block.sourceBlockId === parentBlock.typeOptions.targetBlockId)

  return { displayTitle, displayDescription, isListTemplate }
}
